import React, { useRef, useState } from 'react';
import { X, Trophy, Target, TrendingUp, Award, Share2, Star, Flame, Brain, CheckCircle, Trash2, AlertTriangle, Database, Loader2 } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { shareToPNG } from '../utils/imageExport';
import { useAuth } from '../contexts/AuthContext';

const ProgressModal = ({ isOpen, onClose, stats, achievements, onResetProgress, isLoading = false, isAccumulated = false }) => {
  const modalContentRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { user, profile } = useAuth();

  if (!isOpen) return null;

  const { score, attempts, accuracy, streak, bestStreak, totalQuestions, correctAnswers, incorrectAnswers, regionStats, gamesPlayed } = stats;

  // Prepare data for charts
  const performanceData = [
    { name: 'Correctas', value: correctAnswers, color: '#10b981' },
    { name: 'Incorrectas', value: incorrectAnswers, color: '#ef4444' }
  ];

  const regionPerformanceData = Object.entries(regionStats || {}).map(([region, data]) => ({
    region: region.charAt(0).toUpperCase() + region.slice(1),
    correctas: data.correct || 0,
    incorrectas: data.incorrect || 0,
    precision: data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0
  }));

  const handleShare = async () => {
    if (modalContentRef.current) {
      await shareToPNG(modalContentRef.current, `progreso-anatomia-${Date.now()}.png`);
    }
  };

  const handleDeleteProgress = () => {
    if (onResetProgress) {
      onResetProgress();
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div ref={modalContentRef} className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl">
                <Trophy className="w-8 h-8 text-purple-500" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Mi Progreso</h2>
                  {isLoading && <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />}
                  {isAccumulated && !isLoading && (
                    <span className="px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                      <Database className="w-3 h-3" />
                      Acumulado
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {isAccumulated 
                    ? `Estadísticas totales${gamesPlayed ? ` de ${gamesPlayed} partida${gamesPlayed !== 1 ? 's' : ''}` : ''}`
                    : 'Resumen de la sesión actual'
                  }
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          { (profile || user) && (
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 border-purple-200 bg-gray-100 flex items-center justify-center">
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-gray-300" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-base sm:text-lg font-bold text-gray-800 truncate">{profile?.username || profile?.full_name || 'Usuario'}</p>
                <p className="text-sm text-gray-600 truncate">{user?.email || 'Sin email'}</p>
              </div>
            </div>
          )}

          {/* Key Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-2xl border-2 border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5 text-purple-500" />
                <span className="text-sm font-bold text-gray-600">{isAccumulated ? 'Total' : 'Puntuación'}</span>
              </div>
              <p className="text-3xl font-bold text-purple-600">{score || 0}</p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-2xl border-2 border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-bold text-gray-600">Precisión</span>
              </div>
              <p className="text-3xl font-bold text-blue-600">{accuracy}%</p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-5 rounded-2xl border-2 border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="text-sm font-bold text-gray-600">Racha Actual</span>
              </div>
              <p className="text-3xl font-bold text-orange-600">{streak}</p>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-5 rounded-2xl border-2 border-emerald-200">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-emerald-500" />
                <span className="text-sm font-bold text-gray-600">Mejor Racha</span>
              </div>
              <p className="text-3xl font-bold text-emerald-600">{bestStreak}</p>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Performance Pie Chart */}
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-5 rounded-2xl border-2 border-gray-200">
              <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-500" />
                Rendimiento General
              </h3>
              {attempts > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={performanceData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {performanceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-gray-500">
                  Comienza a responder preguntas para ver tus estadísticas
                </div>
              )}
            </div>

            {/* Region Performance Bar Chart */}
            <div className="bg-gradient-to-br from-gray-50 to-purple-50 p-5 rounded-2xl border-2 border-gray-200">
              <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                Por Región
              </h3>
              {regionPerformanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={regionPerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="region" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="correctas" fill="#10b981" name="Correctas" />
                    <Bar dataKey="incorrectas" fill="#ef4444" name="Incorrectas" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-gray-500">
                  Comienza a responder preguntas para ver tus estadísticas
                </div>
              )}
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-2xl border-2 border-amber-200 mb-6">
            <h3 className="text-xl font-bold text-gray-700 mb-4 flex items-center gap-2">
              <Award className="w-6 h-6 text-amber-500" />
              Logros Desbloqueados
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {achievements.map((achievement, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    achievement.unlocked
                      ? 'bg-white border-amber-300 shadow-md'
                      : 'bg-gray-100 border-gray-300 opacity-50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg ${
                      achievement.unlocked ? 'bg-amber-100' : 'bg-gray-200'
                    }`}>
                      {achievement.unlocked ? (
                        <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
                      ) : (
                        <Star className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-sm text-gray-700">{achievement.title}</h4>
                      <p className="text-xs text-gray-600">{achievement.description}</p>
                    </div>
                  </div>
                  {achievement.unlocked && (
                    <div className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle className="w-3 h-3" />
                      <span>Desbloqueado</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleShare}
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
            >
              <Share2 className="w-5 h-5" />
              Compartir Progreso
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
            >
              <Trash2 className="w-5 h-5" />
              Eliminar Progreso
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-xl font-bold transition-all"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">¿Eliminar Progreso?</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Esta acción eliminará permanentemente todas tus estadísticas, logros y progreso. 
              <strong className="text-red-600"> Esta acción no se puede deshacer.</strong>
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleDeleteProgress}
                className="flex-1 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
              >
                Sí, Eliminar
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-xl font-bold transition-all"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressModal;
